import { Injectable, inject, signal, effect, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService {
  private authService = inject(AuthService);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  // Total idle timeout: 5 minutes (300 seconds)
  private readonly totalTimeoutSeconds = (environment.auth.idleTimeoutMinutes || 5) * 60;
  // Warning appears at 4 minutes, giving 60s countdown
  private readonly warningThresholdSeconds = 60;

  private secondsLeft = this.totalTimeoutSeconds;
  private timerInterval: any = null;
  private lastActivityTime = Date.now();

  isWarningVisible = signal<boolean>(false);
  countdownSeconds = signal<number>(this.warningThresholdSeconds);

  constructor() {
    // Monitor login state
    effect(() => {
      if (this.authService.isLoggedIn()) {
        this.startMonitoring();
      } else {
        this.stopMonitoring();
      }
    });
  }

  private startMonitoring() {
    this.stopMonitoring();
    this.resetTimer();

    // Attach event listeners outside NgZone for performance
    this.ngZone.runOutsideAngular(() => {
      const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
      events.forEach(eventName => {
        window.addEventListener(eventName, this.onUserActivity, { passive: true });
      });

      this.timerInterval = setInterval(() => {
        this.tick();
      }, 1000);
    });
  }

  private stopMonitoring() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(eventName => {
      window.removeEventListener(eventName, this.onUserActivity);
    });

    this.isWarningVisible.set(false);
  }

  private onUserActivity = () => {
    const now = Date.now();
    // Throttle activity resets to every 3 seconds to save cycles
    if (now - this.lastActivityTime > 3000) {
      this.lastActivityTime = now;
      // Only reset timer if warning modal is not active
      if (!this.isWarningVisible()) {
        this.secondsLeft = this.totalTimeoutSeconds;
      }
    }
  };

  private tick() {
    if (!this.authService.isLoggedIn()) {
      this.stopMonitoring();
      return;
    }

    this.secondsLeft--;

    if (this.secondsLeft <= this.warningThresholdSeconds && this.secondsLeft > 0) {
      this.ngZone.run(() => {
        this.isWarningVisible.set(true);
        this.countdownSeconds.set(this.secondsLeft);
      });
    } else if (this.secondsLeft <= 0) {
      this.ngZone.run(() => {
        this.handleTimeout();
      });
    }
  }

  extendSession() {
    this.secondsLeft = this.totalTimeoutSeconds;
    this.isWarningVisible.set(false);
    this.lastActivityTime = Date.now();

    // Refresh token to keep server session fresh
    this.authService.refreshAccessToken().subscribe({
      next: () => {},
      error: () => {}
    });
  }

  logoutNow() {
    this.stopMonitoring();
    this.authService.logout();
    this.router.navigate(['/login'], { queryParams: { reason: 'manual_logout' } });
  }

  private handleTimeout() {
    this.stopMonitoring();
    this.authService.logout();
    this.router.navigate(['/login'], { queryParams: { reason: 'idle_timeout' } });
  }

  resetTimer() {
    this.secondsLeft = this.totalTimeoutSeconds;
    this.lastActivityTime = Date.now();
    this.isWarningVisible.set(false);
    this.countdownSeconds.set(this.warningThresholdSeconds);
  }
}
